package one.devsbox.www.ui.login

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode

@Composable
@ComposableNavNode
fun LoginUI() {
    with(LoginCtx.get()) {
        ContentLoginUI(Modifier.fillMaxSize())
    }
}

@Composable
context(ctx: LoginCtx)
private fun ContentLoginUI(
    modifier: Modifier = Modifier
) {
    var err by remember { mutableStateOf(false) }
    var showPassword by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val animAlpha = remember { Animatable(0f) }
    val animOffsetY = remember { Animatable(30f) }
    LaunchedEffect(Unit) {
        animAlpha.animateTo(1f, animationSpec = tween(800, easing = LinearOutSlowInEasing))
    }
    LaunchedEffect(Unit) {
        animOffsetY.animateTo(0f, animationSpec = tween(800, easing = LinearOutSlowInEasing))
    }

    val infiniteTransition = rememberInfiniteTransition()

    val logoScale by infiniteTransition.animateFloat(
        initialValue = 0.96f,
        targetValue = 1.04f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 2500, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        )
    )

    val borderPulse by infiniteTransition.animateFloat(
        initialValue = 0.3f,
        targetValue = 0.9f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 2000, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        )
    )


    fun doLogin() {
        ctx.vm.action.login(
            onFail = {
                err = true
            }
        )
    }

    val colorPrimary = MaterialTheme.colorScheme.primary
    val colorOutline = MaterialTheme.colorScheme.outlineVariant

    Crossfade(targetState = ctx.vm.loading) { loading ->
        if (loading) {
            FullscreenLoadingUI()
        } else {
            Box(
                modifier = modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background),
                contentAlignment = Alignment.Center
            ) {
                FullscreenLoadingUI(color = colorPrimary)

                // Glassmorphic Premium Form Container Card
                Card(
                    modifier = Modifier
                        .width(420.dp)
                        .padding(16.dp)
                        .offset(y = animOffsetY.value.dp)
                        .graphicsLayer(alpha = animAlpha.value)
                        .border(
                            BorderStroke(
                                1.dp,
                                if (ctx.vm.loading) colorPrimary.copy(alpha = borderPulse)
                                else colorOutline.copy(alpha = 0.4f + 0.4f * borderPulse)
                            ),
                            RoundedCornerShape(12.dp)
                        ),
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.85f)
                    )
                ) {
                    Column(
                        modifier = Modifier.padding(32.dp),
                        verticalArrangement = Arrangement.spacedBy(28.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Premium Brand Header
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(72.dp)
                                    .scale(logoScale)
                                    .clip(CircleShape)
                                    .background(colorPrimary.copy(alpha = 0.1f))
                                    .border(BorderStroke(1.dp, colorPrimary.copy(alpha = 0.2f)), CircleShape),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = Svg.Bsoft,
                                    contentDescription = "BSOFT LOGO",
                                    tint = colorPrimary,
                                    modifier = Modifier.size(40.dp)
                                )
                            }

                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.app_name),
                                    style = MaterialTheme.typography.headlineMedium,
                                    fontWeight = FontWeight.ExtraBold,
                                    letterSpacing = 4.sp,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    fontFamily = FontFamily.Monospace
                                )
                                Text(
                                    text = stringResource(Res.string.moto).uppercase(),
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = FontWeight.SemiBold,
                                    letterSpacing = 1.5.sp,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                                    fontFamily = FontFamily.Monospace
                                )
                            }
                        }

                        // Input field: Login
                        FieldGroup(label = stringResource(Res.string.ui_login_username)) {
                            OutlinedTextField(
                                value = ctx.vm.editorAuth.login.text,
                                onValueChange = {
                                    ctx.vm.editorAuth.login.set(it)
                                    err = false
                                },
                                modifier = Modifier.fillMaxWidth().height(56.dp),
                                shape = RoundedCornerShape(8.dp),
                                leadingIcon = {
                                    Icon(
                                        imageVector = Svg.UserOutline,
                                        contentDescription = null,
                                        tint = colorPrimary.copy(alpha = 0.8f),
                                        modifier = Modifier.size(20.dp)
                                    )
                                },
                                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                                singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = colorPrimary,
                                    unfocusedBorderColor = colorOutline.copy(alpha = 0.5f)
                                )
                            )
                        }

                        // Input field: Password
                        FieldGroup(label = stringResource(Res.string.ui_login_password)) {
                            OutlinedTextField(
                                value = ctx.vm.editorAuth.password.text,
                                onValueChange = {
                                    ctx.vm.editorAuth.password.set(it)
                                    err = false
                                },
                                modifier = Modifier.fillMaxWidth().height(56.dp),
                                shape = RoundedCornerShape(8.dp),
                                leadingIcon = {
                                    Icon(
                                        imageVector = Svg.ShieldAccountVariantOutline,
                                        contentDescription = null,
                                        tint = colorPrimary.copy(alpha = 0.8f),
                                        modifier = Modifier.size(20.dp)
                                    )
                                },
                                trailingIcon = {
                                    IconButton(onClick = { showPassword = !showPassword }) {
                                        Icon(
                                            imageVector = if (showPassword) Svg.EyeOffOutline else Svg.EyeOutline,
                                            contentDescription = if (showPassword) "Hide password" else "Show password",
                                            tint = colorPrimary.copy(alpha = 0.8f),
                                            modifier = Modifier.size(20.dp)
                                        )
                                    }
                                },
                                visualTransformation = if (showPassword) VisualTransformation.None else PasswordVisualTransformation(),
                                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Go),
                                keyboardActions = KeyboardActions(onGo = { doLogin() }),
                                singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = colorPrimary,
                                    unfocusedBorderColor = colorOutline.copy(alpha = 0.5f)
                                )
                            )
                        }

                        // Error Message Section
                        AnimatedVisibility(
                            visible = err,
                            enter = expandVertically(expandFrom = Alignment.Top) + fadeIn(animationSpec = tween(300)),
                            exit = shrinkVertically(shrinkTowards = Alignment.Top) + fadeOut(animationSpec = tween(300))
                        ) {
                            Card(
                                shape = RoundedCornerShape(8.dp),
                                colors = CardDefaults.cardColors(
                                    containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.15f)
                                ),
                                border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.3f)),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.Center
                                ) {
                                    Text(
                                        text = stringResource(Res.string.ui_login_invalid).uppercase(),
                                        color = MaterialTheme.colorScheme.error,
                                        style = MaterialTheme.typography.bodySmall,
                                        fontWeight = FontWeight.Bold,
                                        letterSpacing = 1.sp,
                                        fontFamily = FontFamily.Monospace
                                    )
                                }
                            }
                        }

                        // Authorize Button Group
                        OutlinedButton(
                            enabled = !ctx.vm.loading,
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(50.dp),
                            onClick = { doLogin() },
                            shape = RoundedCornerShape(8.dp),
                            border = BorderStroke(1.5.dp, colorPrimary),
                            colors = ButtonDefaults.outlinedButtonColors(
                                contentColor = colorPrimary
                            )
                        ) {
                            if (ctx.vm.loading) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(20.dp),
                                    strokeWidth = 2.dp,
                                    color = colorPrimary
                                )
                            } else {
                                Text(
                                    text = stringResource(Res.string.ui_login_btn_authorize).uppercase(),
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.labelLarge,
                                    letterSpacing = 2.sp,
                                    fontFamily = FontFamily.Monospace
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    // PASSWORD CHANGE DIALOG POPUP
    if (ctx.vm.tempLoginData != null) {
        PasswordChangeDialog(
            onDismiss = {
                ctx.vm.tempLoginData = null
                ctx.vm.editorPass.password.set(androidx.compose.ui.text.input.TextFieldValue(""))
                ctx.vm.editorPass.passwordRepeat.set(androidx.compose.ui.text.input.TextFieldValue(""))
            }
        )
    }
}

@Composable
context(ctx: LoginCtx)
private fun PasswordChangeDialog(
    onDismiss: () -> Unit
) {
    var dialogLoading by remember { mutableStateOf(false) }
    var dialogError by remember { mutableStateOf("") }
    var showPassword by remember { mutableStateOf(false) }
    var showPasswordRepeat by remember { mutableStateOf(false) }

    val colorPrimary = MaterialTheme.colorScheme.primary
    val colorOutline = MaterialTheme.colorScheme.outlineVariant

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = stringResource(Res.string.ui_login_change_password_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                fontFamily = FontFamily.Monospace,
                letterSpacing = 1.sp
            )
        },
        text = {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    text = stringResource(Res.string.ui_login_change_password_desc),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )

                // Password Field
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        text = stringResource(Res.string.ui_login_change_password_label_new),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        fontFamily = FontFamily.Monospace
                    )
                    OutlinedTextField(
                        value = ctx.vm.editorPass.password.text,
                        onValueChange = {
                            ctx.vm.editorPass.password.set(it)
                            dialogError = ""
                        },
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        shape = RoundedCornerShape(8.dp),
                        placeholder = { Text(stringResource(Res.string.ui_login_change_password_placeholder_new)) },
                        visualTransformation = if (showPassword) VisualTransformation.None else PasswordVisualTransformation(),
                        trailingIcon = {
                            IconButton(onClick = { showPassword = !showPassword }) {
                                Icon(
                                    imageVector = if (showPassword) Svg.EyeOffOutline else Svg.EyeOutline,
                                    contentDescription = if (showPassword) "Hide password" else "Show password",
                                    tint = colorPrimary.copy(alpha = 0.8f),
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                        },
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = colorPrimary,
                            unfocusedBorderColor = colorOutline.copy(alpha = 0.5f)
                        )
                    )
                }

                // Repeat Password Field
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        text = stringResource(Res.string.ui_login_change_password_label_repeat),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        fontFamily = FontFamily.Monospace
                    )
                    OutlinedTextField(
                        value = ctx.vm.editorPass.passwordRepeat.text,
                        onValueChange = {
                            ctx.vm.editorPass.passwordRepeat.set(it)
                            dialogError = ""
                        },
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        shape = RoundedCornerShape(8.dp),
                        placeholder = { Text(stringResource(Res.string.ui_login_change_password_placeholder_repeat)) },
                        visualTransformation = if (showPasswordRepeat) VisualTransformation.None else PasswordVisualTransformation(),
                        trailingIcon = {
                            IconButton(onClick = { showPasswordRepeat = !showPasswordRepeat }) {
                                Icon(
                                    imageVector = if (showPasswordRepeat) Svg.EyeOffOutline else Svg.EyeOutline,
                                    contentDescription = if (showPasswordRepeat) "Hide password" else "Show password",
                                    tint = colorPrimary.copy(alpha = 0.8f),
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                        },
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = colorPrimary,
                            unfocusedBorderColor = colorOutline.copy(alpha = 0.5f)
                        )
                    )
                }

                if (dialogError.isNotEmpty()) {
                    Text(
                        text = dialogError.uppercase(),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        fontWeight = FontWeight.Bold,
                        fontFamily = FontFamily.Monospace
                    )
                }
            }
        },
        confirmButton = {
            TextButton(
                enabled = !ctx.vm.editorPass.password.err && !ctx.vm.editorPass.passwordRepeat.err && !dialogLoading,
                onClick = {
                    dialogLoading = true
                    ctx.vm.action.changePassword(
                        onFail = {
                            dialogLoading = false
                            dialogError = it.message ?: "Failed to change password"
                        }
                    )
                }
            ) {
                if (dialogLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
                } else {
                    Text(stringResource(Res.string.ui_login_change_password_btn_confirm))
                }
            }
        },
        dismissButton = {
            TextButton(
                enabled = !dialogLoading,
                onClick = onDismiss
            ) {
                Text(stringResource(Res.string.ui_login_change_password_btn_cancel))
            }
        }
    )
}

@Composable
private fun FieldGroup(label: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = label.uppercase(),
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.Bold,
            letterSpacing = 1.sp,
            fontFamily = FontFamily.Monospace
        )
        content()
    }
}